import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2,
  Copy,
  FlaskConical,
  KeyRound,
  Plus,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Button, FormModal, SearchBar } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { clientDetailPath } from "../routes";
import { mapClientsRpcError } from "../lib/partnerClients";
import { mapAccountError, type AccountCredentials } from "../lib/partnerAccounts";
import {
  useCreatePartnerClient,
  useCreateReadyPartnerClient,
  useReissuePartnerClientPasscode,
} from "../hooks/usePartnerClients";
import { usePartnerClientsOverview } from "../hooks/usePartnerReports";
import { ClientsTable } from "../components/clients/ClientsTable";
import { ClientCardRow } from "../components/clients/ClientCardRow";
import { CredentialsModal } from "../components/accounts/CredentialsModal";
import { SegmentStrip } from "../components/data/SegmentStrip";
import {
  ClientCreateModal,
  type ClientCreateValues,
} from "../components/clients/ClientCreateModal";
import { useI18n } from "../i18n";

type StatusFilter = "all" | "active" | "trial" | "onboarding";

export default function ClientsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [passcodeModal, setPasscodeModal] = useState<{
    code: string;
    name: string;
  } | null>(null);
  const [credentials, setCredentials] = useState<{
    title: string;
    data: AccountCredentials;
  } | null>(null);

  const clientsQuery = usePartnerClientsOverview();
  const createMutation = useCreatePartnerClient();
  const readyCreateMutation = useCreateReadyPartnerClient();
  const reissueMutation = useReissuePartnerClientPasscode();

  const openReissue = (client: { id: string; name: string }) => {
    reissueMutation.mutate(client.id, {
      onSuccess: (res) => {
        if (res.passcode) {
          setPasscodeModal({ code: res.passcode, name: client.name });
        }
      },
      onError: (err) => toast.error(t(mapClientsRpcError(err))),
    });
  };
  const clients = clientsQuery.data ?? [];  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (statusFilter !== "all" && c.tenant_status.toLowerCase() !== statusFilter) {
        return false;
      }
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.username ?? "").toLowerCase().includes(q) ||
        (c.phone ?? "").toLowerCase().includes(q) ||
        (c.address ?? "").toLowerCase().includes(q)
      );
    });
  }, [clients, search, statusFilter]);

  const stats = useMemo(() => {
    let active = 0;
    let trial = 0;
    let onboarding = 0;
    let openOrders = 0;
    let outstanding = 0;
    for (const c of clients) {
      const status = c.tenant_status.toLowerCase();
      if (status === "active") active += 1;
      else if (status === "trial") trial += 1;
      else if (status === "onboarding") onboarding += 1;
      openOrders += c.active_orders_count;
      outstanding += c.outstanding_pending;
    }
    return { total: clients.length, active, trial, onboarding, openOrders, outstanding };
  }, [clients]);

  const statusTabs: {
    key: StatusFilter;
    label: string;
    icon: LucideIcon;
    count: number;
  }[] = [
    { key: "all", label: t("clients.filterAll"), icon: Users, count: stats.total },
    { key: "active", label: t("clients.active"), icon: CheckCircle2, count: stats.active },
    { key: "trial", label: t("clients.trial"), icon: FlaskConical, count: stats.trial },
    {
      key: "onboarding",
      label: t("clients.onboarding"),
      icon: UserPlus,
      count: stats.onboarding,
    },
  ];

  const handleCreate = async (values: ClientCreateValues) => {
    try {
      if (values.mode === "ready") {
        const ready = await readyCreateMutation.mutateAsync({
          name: values.name,
          phone: values.phone,
          address: values.address,
          username: values.username,
          slug: values.slug,
          password: values.password,
          email: values.email,
        });
        setCreateOpen(false);
        void clientsQuery.refetch();
        setCredentials({ title: values.name, data: ready.credentials });
        return;
      }

      // Existing passcode rail — unchanged.
      const result = await createMutation.mutateAsync({
        name: values.name,
        phone: values.phone,
        address: values.address,
        username: values.username,
        email: values.email,
      });
      setCreateOpen(false);
      void clientsQuery.refetch();
      if (result.passcode) {
        setPasscodeModal({ code: result.passcode, name: values.name });
      } else {
        toast.success(t("clients.toast.created"));
      }
    } catch (err) {
      toast.error(
        t(
          values.mode === "ready"
            ? mapAccountError(err)
            : mapClientsRpcError(err)
        )
      );
    }
  };

  return (
    <div className="menu-page menu-page--wide">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("clients.eyebrow")}</p>
          <h1 className="menu-page__title overview-page__title">
            {t("clients.title")}
            <span className="orders-head__count">{stats.total}</span>
          </h1>
          <p className="menu-page__subtitle overview-page__subtitle">
            {t("clients.subtitle")}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-[var(--text-secondary)]">
              {t("clients.active")}{" "}
              <strong className="text-[var(--text-primary)]">{stats.active}</strong>
            </span>
            <span className="text-[var(--text-secondary)]">
              {t("clients.openOrdersKpi")}{" "}
              <strong className="text-[var(--text-primary)]">{stats.openOrders}</strong>
            </span>
            <span className="text-[var(--text-secondary)]">
              {t("clients.colOutstanding")}{" "}
              <strong className="text-[var(--text-primary)]">
                {formatCurrency(stats.outstanding, "IQD", locale)}
              </strong>
            </span>
          </div>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            className="min-h-10 gap-2 px-4"
          >
            <Plus className="h-4 w-4" />
            {t("clients.add")}
          </Button>
        </div>
      </header>

      <div className="filter-bar">
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("clients.searchPlaceholder")}
          aria-label={t("clients.searchAria")}
          className="max-w-xs"
        />
        <SegmentStrip
          value={statusFilter}
          onChange={(next) => setStatusFilter(next)}
          ariaLabel={t("clients.filtersAria")}
          options={statusTabs}
        />
      </div>

      <section className="menu-panel">
        <div className="menu-panel__body">
          <div className="hidden md:block">
            <ClientsTable
              clients={filtered}
              loading={clientsQuery.isLoading}
              onOpen={(client) => navigate(clientDetailPath(client.id))}
              onReissue={openReissue}
            />
          </div>
          <div className="flex flex-col gap-2 md:hidden">
            {clientsQuery.isLoading ? (
              <div className="h-24 rounded-xl skeleton" />
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-[var(--text-muted)]">
                {t("clients.emptyTitle")}
              </p>
            ) : (
              filtered.map((client) => (
                <div key={client.id} className="flex flex-col gap-1">
                  <ClientCardRow
                    client={client}
                    onOpen={() => navigate(clientDetailPath(client.id))}
                  />
                  {client.tenant_status.toLowerCase() === "onboarding" ? (
                    <button
                      type="button"
                      className="self-end text-xs font-bold text-[var(--accent)]"
                      onClick={() => openReissue(client)}
                    >
                      {t("clients.reissue")}
                    </button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <ClientCreateModal
        open={createOpen}
        busy={createMutation.isPending || readyCreateMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <CredentialsModal
        open={!!credentials}
        title={credentials?.title ?? ""}
        subtitle={t("clients.readyCredentialsHint")}
        credentials={credentials?.data ?? null}
        onClose={() => setCredentials(null)}
      />

      <FormModal
        open={!!passcodeModal}
        onClose={() => setPasscodeModal(null)}
        eyebrow={t("clients.eyebrow")}
        title={t("clients.passcodeTitle")}
        subtitle={t("clients.passcodeModalHint")}
        icon={<KeyRound className="h-5 w-5" />}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button onClick={() => setPasscodeModal(null)}>
              {t("clients.dismiss")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!passcodeModal) return;
                void navigator.clipboard
                  .writeText(passcodeModal.code)
                  .then(() => toast.success(t("common.copied")))
                  .catch(() => toast.error(t("common.copyFailed")));
              }}
            >
              <Copy className="h-4 w-4" />
              {t("common.copy")}
            </Button>
          </div>
        }
      >
        {passcodeModal ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-dashed border-[var(--accent)] bg-[var(--accent-light)] px-6 py-7 text-center">
              <p className="text-xs font-black uppercase tracking-wider text-[var(--text-secondary)]">
                {passcodeModal.name}
              </p>
              <p className="mt-3 font-mono text-4xl font-black tracking-[0.3em] text-[var(--accent)]">
                {passcodeModal.code}
              </p>
            </div>
            <p className="text-sm leading-6 text-[var(--text-secondary)]">
              {t("clients.passcodeModalHint")}
            </p>
          </div>
        ) : null}
      </FormModal>

      <p className="text-xs text-[var(--text-muted)]">{t("clients.note")}</p>
    </div>
  );
}

